/**
 * Analytics layer — thin wrapper that normalises events
 * before shipping to PostHog (or any provider you swap in).
 *
 * Design goals:
 * - Never blocks the UI (fire-and-forget)
 * - Strips PII before sending
 * - Works offline (queues when navigator.onLine = false)
 * - Fully tree-shakeable — import only what you need
 */

const ENABLED = import.meta.env.PROD && !!import.meta.env.VITE_POSTHOG_KEY;
let _posthog = null;

// Lazy-load PostHog only in production
const getPosthog = async () => {
  if (_posthog) return _posthog;
  if (!ENABLED) return null;
  try {
    const { default: posthog } = await import('posthog-js');
    posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
      api_host:          'https://app.posthog.com',
      capture_pageview:  false,       // manual page tracking
      autocapture:       false,       // we control every event
      persistence:       'localStorage',
      sanitize_properties: (props) => {
        // Strip any accidentally included PII
        const safe = { ...props };
        delete safe.email; delete safe.name; delete safe.phone;
        return safe;
      },
    });
    _posthog = posthog;
    return posthog;
  } catch {
    return null;
  }
};

// ── Event catalogue ───────────────────────────────────────────────────────────

export const AnalyticsEvent = {
  // Onboarding
  SIGN_IN:            'sign_in',
  SIGN_IN_ANON:       'sign_in_anonymous',

  // Scan pipeline
  SCAN_STARTED:       'scan_started',
  SCAN_COMPLETED:     'scan_completed',
  SCAN_DEMO_USED:     'scan_demo_used',
  SCAN_FAILED:        'scan_failed',

  // Session lifecycle
  SESSION_CREATED:    'session_created',
  SESSION_JOINED:     'session_joined',
  SESSION_SETTLED:    'session_settled',

  // Claiming
  ITEM_CLAIMED:       'item_claimed',
  ITEM_UNCLAIMED:     'item_unclaimed',
  ITEM_EDITED:        'item_edited',

  // Payment
  PAYMENT_REQUESTED:  'payment_requested',
  PAYMENT_MARKED:     'payment_marked',

  // Settings
  TIP_CHANGED:        'tip_changed',
  SPLIT_MODE_CHANGED: 'split_mode_changed',
  PAYMENT_HANDLE_SET: 'payment_handle_set',

  // Export
  EXPORT_TEXT:        'export_text',
  EXPORT_CSV:         'export_csv',
  EXPORT_HTML:        'export_html',

  // Navigation
  SCREEN_VIEW:        'screen_view',
  QR_VIEWED:          'qr_viewed',
};

// ── Core track function ───────────────────────────────────────────────────────

const queue = [];

export const track = async (event, properties = {}) => {
  const payload = {
    event,
    properties: {
      ...properties,
      timestamp: new Date().toISOString(),
      platform:  window.navigator.standalone ? 'pwa' : 'web',
    },
  };

  if (!navigator.onLine) {
    queue.push(payload);
    return;
  }

  // Flush offline queue first
  while (queue.length) {
    const queued = queue.shift();
    _sendEvent(queued.event, queued.properties);
  }

  _sendEvent(event, payload.properties);
};

const _sendEvent = async (event, properties) => {
  if (!ENABLED) {
    if (import.meta.env.DEV) {
      console.debug(`[Analytics] ${event}`, properties);
    }
    return;
  }
  const ph = await getPosthog();
  ph?.capture(event, properties);
};

// ── Convenience wrappers ──────────────────────────────────────────────────────

export const trackScreenView = (screen) =>
  track(AnalyticsEvent.SCREEN_VIEW, { screen });

export const trackScanCompleted = ({ itemCount, restaurantName, confidence }) =>
  track(AnalyticsEvent.SCAN_COMPLETED, {
    item_count:      itemCount,
    restaurant_name: restaurantName,
    confidence:      Math.round((confidence ?? 0) * 100),
  });

export const trackSessionCreated = ({ participantCount, itemCount }) =>
  track(AnalyticsEvent.SESSION_CREATED, { participant_count: participantCount, item_count: itemCount });

export const trackSessionSettled = ({ participantCount, grandTotal, tipPct, splitMode }) =>
  track(AnalyticsEvent.SESSION_SETTLED, {
    participant_count: participantCount,
    grand_total_bucket: bucketAmount(grandTotal),
    tip_pct:            tipPct,
    split_mode:         splitMode,
  });

export const trackItemClaim = ({ itemId, claimantCount }) =>
  track(AnalyticsEvent.ITEM_CLAIMED, { claimant_count: claimantCount });

export const trackPaymentRequested = ({ method }) =>
  track(AnalyticsEvent.PAYMENT_REQUESTED, { method });

export const trackExport = (format) =>
  track(`export_${format}`);

// Identify user (no PII — only anonymous id + colour)
export const identifyUser = async (userId, avatarColor) => {
  if (!ENABLED) return;
  const ph = await getPosthog();
  ph?.identify(userId, { avatar_color: avatarColor });
};

// ── Utility ───────────────────────────────────────────────────────────────────

/** Bucket amounts so we can't reverse-engineer individual bills */
const bucketAmount = (amount) => {
  if (amount < 50)   return '<$50';
  if (amount < 100)  return '$50-100';
  if (amount < 200)  return '$100-200';
  if (amount < 500)  return '$200-500';
  return '$500+';
};

// Flush offline queue when connection is restored
window.addEventListener('online', () => {
  if (queue.length) track('__flush__');
});
